import { zodResolver } from '@hookform/resolvers/zod';
import {
  ALLERGENS,
  DIETARY_LABELS,
  recipeWriteInputSchema,
  type RecipeDraft,
  type RecipeWriteInput,
} from '@recipes/contracts';
import { normalizeUnit, parseQuantity } from '@recipes/domain';
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
} from '@recipes/ui';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  useFormContext,
  FormProvider,
  type FieldErrors,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { FormField } from '@/features/shared/form-field';
import { allergenLabel, dietaryLabel } from '@/features/shared/labels';

export type DraftFormValues = z.input<typeof recipeWriteInputSchema>;

const toNullableInt = (v: unknown) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : v;
};
const toDecimal = (v: unknown) => {
  if (v === '' || v === null || v === undefined) return null;
  return parseQuantity(String(v)) ?? String(v);
};
const toNullableString = (v: unknown) => (v === '' || v === undefined ? null : v);

/** Move unit codes into the single editable "unit" field so the user sees "g", "tbsp", … */
export function toFormValues(input: RecipeWriteInput): DraftFormValues {
  return {
    ...input,
    ingredientSections: input.ingredientSections.map((s) => ({
      ...s,
      ingredients: s.ingredients.map((i) => ({
        ...i,
        unitText: i.unitCode ?? i.unitText ?? null,
        unitCode: null,
      })),
    })),
  };
}

/** Normalize the free-text unit back into unitCode/unitText before submitting. */
export function fromFormValues(values: RecipeWriteInput): RecipeWriteInput {
  return {
    ...values,
    ingredientSections: values.ingredientSections.map((s) => ({
      ...s,
      ingredients: s.ingredients.map((i) => {
        const raw = (i.unitText ?? '').trim();
        const unit = raw ? normalizeUnit(raw) : undefined;
        return {
          ...i,
          unitCode: unit?.code ?? null,
          unitText: unit ? null : raw || null,
        };
      }),
    })),
  };
}

export interface DraftReviewFormProps {
  initial: RecipeWriteInput;
  draft: RecipeDraft;
  origin: 'IMPORT' | 'AI';
  submitLabel: string;
  submitting?: boolean;
  onSubmit: (input: RecipeWriteInput) => void | Promise<void>;
  /** Rendered between the fields and the submit button (e.g. copy-image checkbox). */
  beforeSubmit?: ReactNode;
  secondaryActions?: ReactNode;
}

export function DraftReviewForm({
  initial,
  draft,
  origin,
  submitLabel,
  submitting,
  onSubmit,
  beforeSubmit,
  secondaryActions,
}: DraftReviewFormProps) {
  const { t } = useTranslation();
  const form = useForm<DraftFormValues, unknown, RecipeWriteInput>({
    resolver: zodResolver(recipeWriteInputSchema),
    defaultValues: toFormValues(initial),
    mode: 'onBlur',
  });
  const { register, handleSubmit, formState } = form;
  const errors = formState.errors;

  const confidenceByName = useMemo(() => {
    const map = new Map<string, RecipeDraft['ingredients'][number]['confidence']>();
    for (const ing of draft.ingredients) map.set(ing.name.trim().toLowerCase(), ing.confidence);
    return map;
  }, [draft.ingredients]);

  return (
    <FormProvider {...form}>
      <form
        noValidate
        onSubmit={handleSubmit((values) => void onSubmit(fromFormValues(values)))}
        className="space-y-8"
        aria-describedby="draft-origin-note"
      >
        <p id="draft-origin-note" className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant={origin === 'AI' ? 'warning' : 'info'}>
            {origin === 'AI' ? t('review.badgeAi') : t('review.badgeImported')}
          </Badge>
          <span className="text-muted-foreground">{t('review.editHint')}</span>
        </p>

        <section aria-labelledby="draft-basics" className="space-y-4">
          <h2 id="draft-basics" className="text-lg font-semibold">
            {t('review.sections.basics')}
          </h2>
          <FormField label={t('review.fields.title')} required error={errors.title?.message}>
            <Input {...register('title')} />
          </FormField>
          <FormField
            label={t('review.fields.description')}
            error={errors.description?.message}
            optionalText={t('common.optional')}
          >
            <Textarea rows={3} {...register('description', { setValueAs: toNullableString })} />
          </FormField>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <FormField
              label={t('review.fields.servings')}
              required
              error={errors.servings?.message}
            >
              <Input
                inputMode="decimal"
                {...register('servings', { setValueAs: (v) => toDecimal(v) ?? '' })}
              />
            </FormField>
            <FormField
              label={t('review.fields.servingsLabel')}
              error={errors.servingsLabel?.message}
            >
              <Input {...register('servingsLabel', { setValueAs: toNullableString })} />
            </FormField>
            <FormField label={t('review.fields.prepTime')} error={errors.prepTimeMinutes?.message}>
              <Input
                type="number"
                min={0}
                inputMode="numeric"
                {...register('prepTimeMinutes', { setValueAs: toNullableInt })}
              />
            </FormField>
            <FormField label={t('review.fields.cookTime')} error={errors.cookTimeMinutes?.message}>
              <Input
                type="number"
                min={0}
                inputMode="numeric"
                {...register('cookTimeMinutes', { setValueAs: toNullableInt })}
              />
            </FormField>
            <FormField label={t('review.fields.restTime')} error={errors.restTimeMinutes?.message}>
              <Input
                type="number"
                min={0}
                inputMode="numeric"
                {...register('restTimeMinutes', { setValueAs: toNullableInt })}
              />
            </FormField>
            <div>
              <Label htmlFor="draft-difficulty" className="mb-1 block">
                {t('review.fields.difficulty')}
              </Label>
              <Controller
                control={form.control}
                name="difficulty"
                render={({ field }) => (
                  <Select
                    value={field.value ?? 'none'}
                    onValueChange={(v) => field.onChange(v === 'none' ? null : v)}
                  >
                    <SelectTrigger id="draft-difficulty">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">–</SelectItem>
                      <SelectItem value="EASY">{t('enums.difficulty.EASY')}</SelectItem>
                      <SelectItem value="MEDIUM">{t('enums.difficulty.MEDIUM')}</SelectItem>
                      <SelectItem value="HARD">{t('enums.difficulty.HARD')}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <FormField label={t('review.fields.cuisine')} error={errors.cuisine?.message}>
              <Input {...register('cuisine', { setValueAs: toNullableString })} />
            </FormField>
            <FormField label={t('review.fields.mealType')} error={errors.mealType?.message}>
              <Input {...register('mealType', { setValueAs: toNullableString })} />
            </FormField>
          </div>
        </section>

        <IngredientSections confidenceByName={confidenceByName} />
        <StepSections />

        <section aria-labelledby="draft-tags" className="space-y-3">
          <h2 id="draft-tags" className="text-lg font-semibold">
            {t('review.sections.tags')}
          </h2>
          <TagsField />
        </section>

        <LabelsSection origin={origin} />

        <section aria-labelledby="draft-notes" className="space-y-3">
          <h2 id="draft-notes" className="text-lg font-semibold">
            {t('review.sections.notes')}
          </h2>
          <FormField
            label={t('review.fields.notes')}
            error={errors.notes?.message}
            optionalText={t('common.optional')}
          >
            <Textarea rows={3} {...register('notes', { setValueAs: toNullableString })} />
          </FormField>
        </section>

        {beforeSubmit}

        {Object.keys(errors).length > 0 && (
          <p role="alert" className="text-sm text-destructive">
            {t('review.fixErrors')}
          </p>
        )}

        <div className="sticky bottom-20 z-10 flex flex-col-reverse gap-2 rounded-lg border bg-background/95 p-3 shadow-md backdrop-blur sm:flex-row sm:justify-end md:bottom-4">
          {secondaryActions}
          <Button type="submit" size="lg" loading={submitting} data-testid="draft-submit">
            {submitLabel}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}

function IngredientSections({
  confidenceByName,
}: {
  confidenceByName: Map<string, 'high' | 'medium' | 'low'>;
}) {
  const { t } = useTranslation();
  const { control, register } = useFormContext<DraftFormValues>();
  const sections = useFieldArray({ control, name: 'ingredientSections' });
  return (
    <section aria-labelledby="draft-ingredients" className="space-y-4">
      <h2 id="draft-ingredients" className="text-lg font-semibold">
        {t('review.sections.ingredients')}
      </h2>
      {sections.fields.map((section, sIdx) => (
        <div key={section.id} className="space-y-2 rounded-lg border p-3">
          {(sections.fields.length > 1 || section.title) && (
            <FormField label={t('review.fields.sectionTitle')}>
              <Input
                {...register(`ingredientSections.${sIdx}.title`, { setValueAs: toNullableString })}
              />
            </FormField>
          )}
          <IngredientRows sectionIndex={sIdx} confidenceByName={confidenceByName} />
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => sections.append({ title: '', ingredients: [] })}
      >
        <Plus aria-hidden /> {t('review.actions.addSection')}
      </Button>
    </section>
  );
}

function IngredientRows({
  sectionIndex,
  confidenceByName,
}: {
  sectionIndex: number;
  confidenceByName: Map<string, 'high' | 'medium' | 'low'>;
}) {
  const { t } = useTranslation();
  const { control, register, formState, watch } = useFormContext<DraftFormValues>();
  const rows = useFieldArray({ control, name: `ingredientSections.${sectionIndex}.ingredients` });
  const sectionErrors = (formState.errors as FieldErrors<DraftFormValues>).ingredientSections?.[
    sectionIndex
  ]?.ingredients;
  return (
    <ul className="space-y-2" aria-label={t('review.sections.ingredients')}>
      {rows.fields.map((row, i) => {
        const base = `ingredientSections.${sectionIndex}.ingredients.${i}` as const;
        const rowErrors = sectionErrors?.[i];
        const name = watch(`${base}.name`);
        const confidence = confidenceByName.get((name ?? '').trim().toLowerCase());
        return (
          <li
            key={row.id}
            className={cn(
              'grid grid-cols-[minmax(0,4.5rem)_minmax(0,5rem)_1fr] gap-2 rounded-md border p-2 md:grid-cols-[5rem_6rem_1fr_1fr_auto_auto]',
              confidence && confidence !== 'high' && 'border-warning bg-warning/10',
            )}
          >
            <FormField label={t('review.fields.quantity')} error={rowErrors?.quantity?.message}>
              <Input
                inputMode="decimal"
                {...register(`${base}.quantity`, { setValueAs: toDecimal })}
              />
            </FormField>
            <FormField label={t('review.fields.unit')} error={rowErrors?.unitText?.message}>
              <Input {...register(`${base}.unitText`, { setValueAs: toNullableString })} />
            </FormField>
            <FormField label={t('review.fields.name')} required error={rowErrors?.name?.message}>
              <Input {...register(`${base}.name`)} />
            </FormField>
            <FormField
              label={t('review.fields.note')}
              error={rowErrors?.note?.message}
              className="col-span-3 md:col-span-1"
            >
              <Input {...register(`${base}.note`, { setValueAs: toNullableString })} />
            </FormField>
            <div className="col-span-2 flex items-end gap-2 md:col-span-1">
              <Controller
                control={control}
                name={`${base}.optional`}
                render={({ field }) => (
                  <label className="flex min-h-[40px] items-center gap-2 text-sm">
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(c) => field.onChange(c === true)}
                    />
                    {t('common.optional')}
                  </label>
                )}
              />
              {confidence && confidence !== 'high' && (
                <Badge variant="warning">{t('review.lowConfidence')}</Badge>
              )}
            </div>
            <div className="flex items-end justify-end">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('review.actions.removeIngredient')}
                onClick={() => rows.remove(i)}
              >
                <Trash2 aria-hidden />
              </Button>
            </div>
          </li>
        );
      })}
      <li>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            rows.append({
              name: '',
              quantity: null,
              unitText: null,
              note: null,
              optional: false,
              scalable: true,
            })
          }
        >
          <Plus aria-hidden /> {t('review.actions.addIngredient')}
        </Button>
      </li>
    </ul>
  );
}

function StepSections() {
  const { t } = useTranslation();
  const { control, register } = useFormContext<DraftFormValues>();
  const sections = useFieldArray({ control, name: 'stepSections' });
  return (
    <section aria-labelledby="draft-steps" className="space-y-4">
      <h2 id="draft-steps" className="text-lg font-semibold">
        {t('review.sections.steps')}
      </h2>
      {sections.fields.map((section, sIdx) => (
        <div key={section.id} className="space-y-2 rounded-lg border p-3">
          {(sections.fields.length > 1 || section.title) && (
            <FormField label={t('review.fields.sectionTitle')}>
              <Input
                {...register(`stepSections.${sIdx}.title`, { setValueAs: toNullableString })}
              />
            </FormField>
          )}
          <StepRows sectionIndex={sIdx} />
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => sections.append({ title: '', steps: [] })}
      >
        <Plus aria-hidden /> {t('review.actions.addSection')}
      </Button>
    </section>
  );
}

function StepRows({ sectionIndex }: { sectionIndex: number }) {
  const { t } = useTranslation();
  const { control, register, formState } = useFormContext<DraftFormValues>();
  const rows = useFieldArray({ control, name: `stepSections.${sectionIndex}.steps` });
  const sectionErrors = (formState.errors as FieldErrors<DraftFormValues>).stepSections?.[
    sectionIndex
  ]?.steps;
  return (
    <ol className="space-y-2" aria-label={t('review.sections.steps')}>
      {rows.fields.map((row, i) => {
        const base = `stepSections.${sectionIndex}.steps.${i}` as const;
        return (
          <li key={row.id} className="grid grid-cols-[auto_1fr_auto] gap-2 rounded-md border p-2">
            <span className="pt-2 text-sm font-semibold tabular-nums" aria-hidden>
              {i + 1}.
            </span>
            <div className="space-y-2">
              <FormField
                label={`${t('review.fields.step')} ${i + 1}`}
                required
                error={sectionErrors?.[i]?.instruction?.message}
              >
                <Textarea rows={2} {...register(`${base}.instruction`)} />
              </FormField>
              <FormField label={t('review.fields.durationMinutes')} className="max-w-[10rem]">
                <Input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  {...register(`${base}.durationSeconds`, {
                    setValueAs: (v) => {
                      const n = toNullableInt(v);
                      return typeof n === 'number' ? n * 60 : n;
                    },
                  })}
                  defaultValue={row.durationSeconds ? Math.round(row.durationSeconds / 60) : ''}
                />
              </FormField>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('review.actions.removeStep')}
              onClick={() => rows.remove(i)}
            >
              <Trash2 aria-hidden />
            </Button>
          </li>
        );
      })}
      <li>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => rows.append({ instruction: '', durationSeconds: null })}
        >
          <Plus aria-hidden /> {t('review.actions.addStep')}
        </Button>
      </li>
    </ol>
  );
}

function TagsField() {
  const { t } = useTranslation();
  const { control } = useFormContext<DraftFormValues>();
  return (
    <Controller
      control={control}
      name="tags"
      render={({ field }) => (
        <TagsInput
          value={field.value ?? []}
          onChange={field.onChange}
          label={t('review.fields.tags')}
          hint={t('review.tagsHint')}
        />
      )}
    />
  );
}

function TagsInput({
  value,
  onChange,
  label,
  hint,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  label: string;
  hint: string;
}) {
  const [text, setText] = useState(value.join(', '));
  return (
    <FormField label={label} hint={hint}>
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() =>
          onChange(
            [
              ...new Set(
                text
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              ),
            ].slice(0, 50),
          )
        }
      />
    </FormField>
  );
}

function LabelsSection({ origin }: { origin: 'IMPORT' | 'AI' }) {
  const { t } = useTranslation();
  const { control } = useFormContext<DraftFormValues>();
  const labels = useFieldArray({ control, name: 'labels' });
  const [addKind, setAddKind] = useState<'DIETARY' | 'ALLERGEN'>('DIETARY');
  const [addValue, setAddValue] = useState('');
  const options = addKind === 'DIETARY' ? DIETARY_LABELS : ALLERGENS;
  const labelText = (kind: string, value: string) =>
    kind === 'DIETARY' ? dietaryLabel(t, value) : allergenLabel(t, value);

  return (
    <section aria-labelledby="draft-labels" className="space-y-3">
      <h2 id="draft-labels" className="text-lg font-semibold">
        {t('review.sections.labels')}
      </h2>
      <p className="text-sm text-muted-foreground">
        {origin === 'AI' ? t('review.labelsHintAi') : t('review.labelsHintImport')}
      </p>
      {labels.fields.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('review.noLabels')}</p>
      )}
      <ul className="space-y-2">
        {labels.fields.map((label, i) => (
          <li key={label.id} className="flex flex-wrap items-center gap-3 rounded-md border p-2">
            <Badge variant="outline">{t(`enums.labelKind.${label.kind}`)}</Badge>
            <span className="font-medium">{labelText(label.kind, label.value)}</span>
            <Controller
              control={control}
              name={`labels.${i}.confirmed`}
              render={({ field }) => (
                <label className="flex min-h-[40px] items-center gap-2 text-sm">
                  <Checkbox
                    checked={Boolean(field.value)}
                    onCheckedChange={(c) => field.onChange(c === true)}
                  />
                  {field.value ? t('review.labelConfirmed') : t('review.labelVerify')}
                </label>
              )}
            />
            {!label.confirmed && label.source !== 'USER' && (
              <Badge variant="warning">{t('review.suggestedVerify')}</Badge>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto"
              aria-label={t('review.actions.removeLabel')}
              onClick={() => labels.remove(i)}
            >
              <Trash2 aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label htmlFor="add-label-kind">{t('review.fields.labelKind')}</Label>
          <Select
            value={addKind}
            onValueChange={(v) => {
              setAddKind(v as 'DIETARY' | 'ALLERGEN');
              setAddValue('');
            }}
          >
            <SelectTrigger id="add-label-kind" className="mt-1 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DIETARY">{t('enums.labelKind.DIETARY')}</SelectItem>
              <SelectItem value="ALLERGEN">{t('enums.labelKind.ALLERGEN')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="add-label-value">{t('review.fields.labelValue')}</Label>
          <Select value={addValue} onValueChange={setAddValue}>
            <SelectTrigger id="add-label-value" className="mt-1 w-48">
              <SelectValue placeholder="–" />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o} value={o}>
                  {labelText(addKind, o)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={!addValue}
          onClick={() => {
            if (!addValue) return;
            labels.append({ kind: addKind, value: addValue, source: 'USER', confirmed: true });
            setAddValue('');
          }}
        >
          <Plus aria-hidden /> {t('review.actions.addLabel')}
        </Button>
      </div>
    </section>
  );
}
